import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Eye, Loader2, Save, FileText, Mail, Phone, Globe, Landmark, MapPin, Hash, RotateCcw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { FeatureToggles } from "@/components/settings/FeatureToggles";
import { DangerZone } from "@/components/settings/DangerZone";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import api from "@/lib/api";

const FIELDS = [
  { key: "name", label: "Firmanamn", icon: Building2, placeholder: "Vest Maskinservice AS", required: true, hint: "Vist i footer på alle PDF-rapportar." },
  { key: "org_number", label: "Organisasjonsnummer", icon: Hash, placeholder: "123 456 789" },
  { key: "address", label: "Adresse", icon: MapPin, placeholder: "Industrivegen 12" },
  { key: "postal", label: "Postnr og stad", icon: MapPin, placeholder: "5300 Kleppestø" },
  { key: "phone", label: "Telefon", icon: Phone, placeholder: "+47 414 84 929" },
  { key: "email", label: "E-post", icon: Mail, placeholder: "post@vestmaskinservice.no", type: "email" },
  { key: "web", label: "Nettside", icon: Globe, placeholder: "vestmaskinservice.no" },
  { key: "bank_account", label: "Kontonummer", icon: Landmark, placeholder: "1234 56 78903", hint: "Valfritt – berre om det skal stå på rapporten." },
];

const Field = ({ f, value, onChange }) => {
  const Icon = f.icon;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`company-${f.key}`} className="text-sm font-semibold">
        {f.label}{f.required && <span className="text-primary"> *</span>}
      </Label>
      <div className="relative">
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input id={`company-${f.key}`} type={f.type || "text"} value={value || ""} onChange={(e) => onChange(f.key, e.target.value)} placeholder={f.placeholder} className="h-11 pl-9" data-testid={`company-${f.key}-input`} />
      </div>
      {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
    </div>
  );
};

export default function Innstillingar() {
  const { isAdmin } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/settings/company").then((r) => { setProfile(r.data); setForm(r.data); }).catch(() => toast.error("Kunne ikkje hente firmaprofil.")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = profile && FIELDS.concat([{ key: "tagline" }, { key: "footer_note" }]).some((f) => (form[f.key] || "") !== (profile[f.key] || ""));

  const save = async (e) => {
    e?.preventDefault();
    if (!(form.name || "").trim()) return toast.error("Firmanamn er påkrevd.");
    setSaving(true);
    try {
      const r = await api.put("/settings/company", form);
      setProfile(r.data);
      setForm(r.data);
      toast.success("Firmaprofil lagra. Alle nye PDF-ar brukar dei nye opplysningane.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikkje lagre.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="settings-page">
      <PageHeader
        title="Innstillingar"
        description="Firmaprofil for PDF-rapportar, valfrie funksjonar og vedlikehald av data."
        actions={
          <Button variant="outline" onClick={() => setPreview(true)} data-testid="settings-preview-sample-button">
            <Eye /> Sjå eksempel-PDF
          </Button>
        }
      />

      {loading ? (
        <Card className="shadow-sm"><CardContent className="grid gap-4 p-6 sm:grid-cols-2">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-11" />)}</CardContent></Card>
      ) : (
        <form onSubmit={save} className="grid gap-5 lg:grid-cols-[1fr_320px]" data-testid="company-profile-form">
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 font-heading text-base font-bold"><Building2 className="h-4 w-4 text-primary" /> Firmaprofil</CardTitle>
              <CardDescription>Opplysningane står nedst på kvar side i PDF-en, saman med sidetal.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                {FIELDS.map((f) => <Field key={f.key} f={f} value={form[f.key]} onChange={change} />)}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="company-tagline" className="text-sm font-semibold">Slagord / kort skildring</Label>
                <Input id="company-tagline" value={form.tagline || ""} onChange={(e) => change("tagline", e.target.value)} placeholder="Service og vedlikehald av maskiner og utstyr" className="h-11" data-testid="company-tagline-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="company-footer" className="text-sm font-semibold">Tilleggstekst i footer <span className="font-normal text-muted-foreground">– valfri</span></Label>
                <Textarea id="company-footer" rows={2} value={form.footer_note || ""} onChange={(e) => change("footer_note", e.target.value)} placeholder="T.d. «Godkjent servicepartnar» eller ein kort referanse til vilkår." data-testid="company-footer-input" />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
                <p className="text-xs text-muted-foreground" data-testid="company-updated-at">
                  {profile?.updated_at ? `Sist endra ${formatDate(profile.updated_at)}${profile.updated_by ? ` av ${profile.updated_by}` : ""}` : "Standardverdiar – ikkje lagra enno."}
                </p>
                <div className="flex gap-2">
                  {dirty && <Button type="button" variant="ghost" onClick={() => setForm(profile)} data-testid="company-reset-button"><RotateCcw /> Angre</Button>}
                  <Button type="submit" disabled={saving || !isAdmin || !dirty} data-testid="company-save-button">
                    {saving ? <Loader2 className="animate-spin" /> : <Save />} Lagre firmaprofil
                  </Button>
                </div>
              </div>
              {!isAdmin && <p className="text-xs text-muted-foreground">Berre administrator kan endre firmaprofilen.</p>}
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card className="border-transparent bg-[#0B0D10] text-white shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/60"><FileText className="h-4 w-4" /> Slik ser footeren ut</div>
                <div className="mt-3 rounded-md bg-white p-3 text-[#0B0D10]" data-testid="company-footer-preview">
                  <div className="text-[11px]"><span className="font-bold">{form.name || "Firmanamn"}</span>
                    <span className="text-[#6B7280]">{[form.address, form.postal, form.org_number ? `Org.nr ${form.org_number}` : null].filter(Boolean).map((x) => `  ·  ${x}`).join("")}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-[#6B7280]">{[form.phone ? `Tlf ${form.phone}` : null, form.email, form.web, form.footer_note].filter(Boolean).join("  ·  ") || "Telefon · e-post · nettside"}</div>
                </div>
                <p className="mt-3 text-xs text-white/60">Logo og fargar kjem frå merkevareprofilen og blir ikkje endra her.</p>
                <Button type="button" variant="secondary" className="mt-4 w-full bg-white text-[#0B0D10] hover:bg-white/90" onClick={() => setPreview(true)} data-testid="settings-preview-sample-button-side"><Eye /> Førehandsvis eksempel</Button>
              </CardContent>
            </Card>
          </div>
        </form>
      )}

      {isAdmin && (
        <div className="grid gap-5 lg:grid-cols-2" data-testid="settings-admin-section">
          <FeatureToggles />
          <DangerZone />
        </div>
      )}

      <PdfPreviewDialog open={preview} onOpenChange={setPreview} sample title="Eksempel på servicerapport" />
    </div>
  );
}
